import {
  createContext,
  useContext,
  useState,
  useEffect,
  useCallback,
  useRef,
} from "react";
import {
  uploadPlaybookToFirestore,
  assignPlaybookToPlayersInFirestore,
  getPlayersByTeamId,
  invitePlayerByEmail,
  removePlayerFromTeam,
  removePlaybookFromFirestore,
  syncOrphanedPlaybooks,
} from "../lib/firestore";
import { doc, getDoc } from "firebase/firestore";
import { db } from "../lib/firebase";

// Create context
const DataContext = createContext();

// Provider component
export const DataProvider = ({ children, user }) => {
  const [players, setPlayers] = useState([]);
  const [playbooks, setPlaybooks] = useState([]);
  const [loadingPlayers, setLoadingPlayers] = useState(false);
  const [loadingPlaybooks, setLoadingPlaybooks] = useState(false);
  // Ensures the orphan sync runs only once per session, not on every re-render
  const hasSynced = useRef(false);

  const fetchPlayers = useCallback(async () => {
    if (!user?.uid) return;

    setLoadingPlayers(true);
    try {
      // First get the coach's teamId
      const coachRef = doc(db, "coach", user.uid);
      const coachDoc = await getDoc(coachRef);

      if (!coachDoc.exists()) {
        setPlayers([]);
        return;
      }

      const coachData = coachDoc.data();
      const teamId = coachData.profile?.team;

      if (!teamId) {
        setPlayers([]);
        return;
      }

      // Fetch players that match the coach's teamId
      const teamPlayers = await getPlayersByTeamId(teamId);
      if (teamPlayers.length > 0) {
        const normalized = teamPlayers.map((player) => {
          const entries = Array.isArray(player.playBook) ? player.playBook : [];

          const rawEntries = entries.filter(Boolean);

          const playBookEntries = rawEntries.map((entry) => ({
            id: entry?.id ?? null,
            title: entry?.title ?? entry?.name ?? "Untitled",
            viewed: Boolean(entry?.viewed),
            lastViewed: entry?.lastViewed ?? null,
            category: entry?.category ?? null,
            duration: entry?.duration ?? null,
            install: entry?.install ?? null,
            pdfUrl: (entry?.pdfUrl ?? entry?.pdf) || null,
            uploadDate: entry?.uploadDate ?? entry?.uploadedAt ?? null,
            totalTimeSpentMinutes: entry?.totalTimeSpentMinutes ?? 0,
          }));

          const assignedPlaybooks = playBookEntries
            .map((e) => e.id)
            .filter((id) => id !== null && id !== undefined);

          const displayName = player.profile?.name || player.name || "Unknown";
          const email = player.email || player.profile?.email || "";

          return {
            id: player.id,
            name: displayName,
            email,
            assignedPlaybooks,
            playBookEntries,
            profile: player.profile || {},
            role: player.role || "player",
            coachId: player.coachId,
          };
        });

        setPlayers(normalized);
      } else {
        setPlayers([]);
      }
    } catch (err) {
      setPlayers([]);
    } finally {
      setLoadingPlayers(false);
    }
  }, [user]);

  const fetchPlaybooks = useCallback(async () => {
    if (!user?.uid) return;

    setLoadingPlaybooks(true);
    try {
      const coachRef = doc(db, "coach", user.uid);
      const coachDoc = await getDoc(coachRef);

      if (coachDoc.exists()) {
        const coachData = coachDoc.data();
        setPlaybooks(coachData.playBook || []);
      } else {
        setPlaybooks([]);
      }
    } catch (err) {
      setPlaybooks([]);
    } finally {
      setLoadingPlaybooks(false);
    }
  }, [user]);

  // On mount: fetch data, then run a one-time orphan sync to clean up any
  // player documents that still contain playbooks deleted before the fix.
  useEffect(() => {
    const init = async () => {
      await fetchPlaybooks();
      await fetchPlayers();

      if (!hasSynced.current && user?.uid) {
        hasSynced.current = true;
        await syncOrphanedPlaybooks(user.uid);
        // Re-fetch players so the UI reflects any cleaned-up data
        await fetchPlayers();
      }
    };
    init();
  }, [fetchPlayers, fetchPlaybooks, user]);

  // Helper functions
  const getPlayerById = (id) => players.find((player) => player.id === id);

  const getPlaybookById = (id) => playbooks.find((p) => p.id === id);

  const getPlaybookPlayers = (playbookId) => {
    return players.filter(
      (player) =>
        player.assignedPlaybooks &&
        player.assignedPlaybooks.includes(playbookId)
    );
  };

  // Compute detailed engagement rows from players' playBookEntries
  const getEngagementData = () => {
    const rows = [];
    players.forEach((player) => {
      (player.playBookEntries || []).forEach((entry) => {
        const last = entry?.lastViewed;
        const lastDate = last
          ? last.toDate
            ? last.toDate()
            : typeof last === "string"
              ? new Date(last)
              : last
          : null;
        rows.push({
          id: `${player.id}:${entry.id ?? entry.title}`,
          playbookId: entry.id,
          playbookName: entry.title,
          playerId: player.id,
          playerName: player.name,
          opened: Boolean(entry.viewed),
          lastOpened: lastDate ? lastDate.toISOString() : null,
          totalTimeSpentMinutes: entry.totalTimeSpentMinutes ?? 0,
        });
      });
    });
    return rows;
  };

  const getPlaybookEngagementStats = () => {
    const statsMap = new Map();
    const rows = getEngagementData();

    rows.forEach((row) => {
      const key = row.playbookId ?? row.playbookName;
      if (!statsMap.has(key)) {
        statsMap.set(key, {
          playbookId: row.playbookId,
          playbookName: row.playbookName,
          assignedCount: 0,
          openedCount: 0,
          openRate: 0,
        });
      }
      const stat = statsMap.get(key);
      stat.assignedCount += 1;
      if (row.opened) stat.openedCount += 1;
    });

    statsMap.forEach((stat) => {
      stat.openRate = stat.assignedCount
        ? Math.round((stat.openedCount / stat.assignedCount) * 100)
        : 0;
    });

    const result = {};
    statsMap.forEach((value, key) => {
      result[key] = value;
    });
    return result;
  };

  const assignPlaybookToPlayers = async (playbookId, playerIds) => {
    try {
      const playbook = playbooks.find((p) => p.id === playbookId);
      if (!playbook) throw new Error("Playbook not found");

      await assignPlaybookToPlayersInFirestore(playbookId, playerIds, playbook);

      await fetchPlayers();
      await fetchPlaybooks();
    } catch (error) {
      throw error;
    }
  };

  const uploadPlaybook = async (file, name, selectedPlayerIds, install) => {
    try {
      const newPlaybook = await uploadPlaybookToFirestore(
        file,
        name,
        user.uid,
        selectedPlayerIds,
        install
      );

      setPlaybooks((prev) => [...prev, newPlaybook]);

      await fetchPlayers();

      return newPlaybook;
    } catch (error) {
      throw error;
    }
  };

  const invitePlayer = async (playerEmail) => {
    try {
      if (!user?.uid) {
        throw new Error("User not authenticated");
      }

      // Get the coach's team ID
      const coachRef = doc(db, "coach", user.uid);
      const coachDoc = await getDoc(coachRef);

      if (!coachDoc.exists()) {
        throw new Error("Coach profile not found");
      }

      const coachData = coachDoc.data();
      const teamId = coachData.profile?.team;

      if (!teamId) {
        throw new Error("Team ID not found in coach profile");
      }

      // Invite the player
      const result = await invitePlayerByEmail(playerEmail, user.uid, teamId);

      // Refresh the players list
      await fetchPlayers();

      return result;
    } catch (error) {
      throw error;
    }
  };

  const removePlayer = async (playerId) => {
    try {
      if (!user?.uid) {
        throw new Error("User not authenticated");
      }

      // Remove the player from the team and clean up coach's playbook assignments
      const result = await removePlayerFromTeam(playerId, user.uid);

      // Refresh the players list
      await fetchPlayers();

      return result;
    } catch (error) {
      throw error;
    }
  };

  const removePlaybook = async (playbookId) => {
    try {
      if (!user?.uid) {
        throw new Error("User not authenticated");
      }

      // Remove the playbook from coach and all assigned players
      const result = await removePlaybookFromFirestore(playbookId, user.uid);

      // Refresh both playbooks and players lists
      await fetchPlaybooks();
      await fetchPlayers();

      return result;
    } catch (error) {
      throw error;
    }
  };

  const value = {
    players,
    loadingPlayers,
    fetchPlayers,
    playbooks,
    loadingPlaybooks,
    fetchPlaybooks,
    getPlayerById,
    getPlaybookById,
    getPlaybookPlayers,
    getEngagementData,
    getPlaybookEngagementStats,
    assignPlaybookToPlayers,
    uploadPlaybook,
    invitePlayer,
    removePlayer,
    removePlaybook,
  };

  return <DataContext.Provider value={value}>{children}</DataContext.Provider>;
};

// Custom hook to use the context
export const useData = () => {
  const context = useContext(DataContext);
  if (!context) {
    throw new Error("useData must be used within a DataProvider");
  }
  return context;
};
